import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useT } from "../lib/i18n";
import { PageHeader, Field, NativeSelect } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { StaffPermissions } from "../components/StaffPermissions";
import { AlertPrefs } from "../components/AccountSwitch";

const ROLES = ["owner", "professional", "secretary", "accountant"];
const BLANK = { name: "", email: "", password: "", role: "professional", title: "", phone: "", location_ids: [], color: "#e6007e", active: true, home_only: false, work_mode: "both", perms: null };

function StaffEditor({ open, onOpenChange, member, onSaved }) {
  const { locations, user } = useApp();
  const { t } = useT();
  const [f, setF] = useState(BLANK);
  useEffect(() => { if (open) setF({ ...BLANK, ...(member || {}), password: "" }); }, [open, member]);
  const toggleLoc = (id) => setF({ ...f, location_ids: f.location_ids.includes(id) ? f.location_ids.filter((x) => x !== id) : [...f.location_ids, id] });

  const save = async () => {
    const body = { ...f, password: f.password || null };
    ["id", "created_at", "schedule", "clinic_id", "alert_email", "alert_phone", "alert_email_on", "alert_whatsapp_on"].forEach((k) => delete body[k]);
    try {
      member?.id ? await api.put(`/staff/${member.id}`, body) : await api.post("/staff", body);
      toast.success("Staff saved"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const self = member?.id === user.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="staff-editor">
        <DialogHeader><DialogTitle>{t("staff")}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          {["name", "email", "title", "phone"].map((k) => (
            <Field key={k} label={t(k)}><Input data-testid={`staff-${k}-input`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></Field>
          ))}
          <Field label={`${t("password")}${member?.id ? " (leave blank to keep)" : ""}`}><Input data-testid="staff-password-input" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
          <Field label={t("role")}>
            <NativeSelect testId="staff-role-select" value={f.role} onChange={(v) => setF({ ...f, role: v, perms: null })} className={self ? "pointer-events-none opacity-60" : ""}
              options={ROLES.map((r) => ({ value: r, label: t(`role_${r}`) }))} />
          </Field>
          <Field label={t("color")}><Input type="color" className="h-10 p-1" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} /></Field>
          <label className="flex items-center gap-3 text-sm pt-6"><Switch disabled={self} data-testid="staff-active-switch" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} />{t("active")}</label>
          {["professional", "owner"].includes(f.role) && <div className="sm:col-span-2 space-y-1.5"><p className="text-xs text-muted-foreground">Works</p><div className="flex flex-wrap gap-2" data-testid="staff-work-mode">
            {[["both", "Clinic & home visits"], ["clinic", "Clinic only"], ["home", "Home visits only"]].map(([v, label]) => (
              <button key={v} type="button" data-testid={`staff-work-mode-${v}`} onClick={() => setF({ ...f, work_mode: v, home_only: v === "home" })}
                className={`rounded-full border px-4 py-1.5 text-sm ${(f.work_mode || (f.home_only ? "home" : "both")) === v ? "bg-primary text-primary-foreground border-primary" : "bg-card"}`}>{label}</button>))}
          </div></div>}
          <div className="sm:col-span-2">
            <p className="text-xs text-muted-foreground mb-2">{t("locations")}</p>
            <div className="flex flex-wrap gap-4">
              {locations.map((l) => (
                <label key={l.id} className="flex items-center gap-2 text-sm"><Checkbox data-testid={`staff-loc-${l.id}`} checked={f.location_ids.includes(l.id)} onCheckedChange={() => toggleLoc(l.id)} />{l.name}</label>
              ))}
            </div>
          </div>
          {!self && <StaffPermissions role={f.role} perms={f.perms} onChange={(p) => setF({ ...f, perms: p })} />}
          {member?.id && ["owner", "professional"].includes(f.role) && <Link to={`/app/availability?user=${member.id}`} className="sm:col-span-2 text-sm text-primary underline" data-testid="staff-availability-link">Working hours & time off</Link>}
          {member?.id && <div className="border-t pt-4"><AlertPrefs path={`/staff/${member.id}/alerts`} /></div>}
        </div>
        <Button className="rounded-full mt-2" data-testid="staff-save-button" disabled={!f.name || !f.email} onClick={save}>{t("save")}</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Staff() {
  const { staff, locations, reload } = useApp();
  const { t } = useT();
  const [edit, setEdit] = useState(null);
  return (
    <div>
      <PageHeader title={t("staff")}><Button className="rounded-full" data-testid="new-staff-btn" onClick={() => setEdit({})}>{t("create")}</Button></PageHeader>
      <div className="rounded-[var(--radius)] border bg-card divide-y" data-testid="staff-list">
        {staff.map((s) => (
          <button key={s.id} onClick={() => setEdit(s)} data-testid={`staff-row-${s.id}`} className={`w-full text-left flex flex-wrap items-center gap-4 px-6 py-4 hover:bg-muted/40 ${s.active ? "" : "opacity-50"}`}>
            <span className="h-9 w-9 rounded-full grid place-items-center text-white text-sm font-bold" style={{ background: s.color }}>{s.name[0]}</span>
            <div className="flex-1 min-w-[160px]"><div className="font-semibold">{s.name}</div><div className="text-xs text-muted-foreground">{s.email}</div></div>
            <span className="text-xs text-muted-foreground">{locations.filter((l) => s.location_ids?.includes(l.id)).map((l) => l.name).join(", ")}</span>
            <span className="rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium">{t(`role_${s.role}`)}</span>
          </button>
        ))}
      </div>
      <StaffEditor open={!!edit} onOpenChange={(o) => !o && setEdit(null)} member={edit} onSaved={reload} />
    </div>
  );
}
